import React, { useState } from 'react';
import { Result, Button, Space, Input, App, Typography } from 'antd';
import {
  FilePdfOutlined,
  FileTextOutlined,
  MailOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { getFacturaPdfUrl, getFacturaXmlUrl, useSendFacturaEmailMutation } from '../../services/autofacturaApi';
import type { AutofacturaResult } from '../../types/autofactura';

const { Text } = Typography;

interface ResultStepProps {
  result: AutofacturaResult;
  code: string;
  onReset: () => void;
  onRetryCorrection?: () => void;
}

const ResultStep: React.FC<ResultStepProps> = ({ result, code, onReset, onRetryCorrection }) => {
  const { message } = App.useApp();
  const [email, setEmail] = useState('');
  const [sendEmail, { isLoading: sendingEmail }] = useSendFacturaEmailMutation();

  const handleSendEmail = async () => {
    if (!email || !result.facturaId) return;
    try {
      await sendEmail({ code, facturaId: result.facturaId, email }).unwrap();
      message.success('Factura enviada por correo');
    } catch {
      message.error('Error al enviar el correo');
    }
  };

  const handleDownloadPdf = () => {
    if (!result.facturaId) return;
    window.open(getFacturaPdfUrl(code, result.facturaId), '_blank');
  };

  const handleDownloadXml = () => {
    if (!result.facturaId) return;
    window.open(getFacturaXmlUrl(code, result.facturaId), '_blank');
  };

  if (!result.success) {
    const extraButtons = [];

    if (onRetryCorrection) {
      extraButtons.push(
        <Button
          key="correct"
          type="primary"
          onClick={onRetryCorrection}
          style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
        >
          Corregir datos fiscales
        </Button>
      );
    }

    extraButtons.push(
      <Button
        key="retry"
        type={onRetryCorrection ? 'default' : 'primary'}
        onClick={onReset}
        style={onRetryCorrection ? undefined : { backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
      >
        Intentar de nuevo
      </Button>
    );

    return (
      <Result
        status="error"
        icon={<CloseCircleOutlined />}
        title="No se pudo generar la factura"
        subTitle={result.error || 'Ocurrió un error inesperado. Por favor contacta al restaurante para asistencia.'}
        extra={extraButtons}
      />
    );
  }

  return (
    <div>
      <Result
        status="success"
        icon={<CheckCircleOutlined style={{ color: 'var(--color-primary)' }} />}
        title="Factura generada exitosamente"
        subTitle={
          <div>
            <p>Folio: <strong>{result.folio}</strong></p>
            {result.uuid && <p style={{ fontSize: 12 }}>UUID: {result.uuid}</p>}
            {result.total && <p>Total: <strong>${result.total.toFixed(2)}</strong></p>}
          </div>
        }
      />

      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space style={{ width: '100%', justifyContent: 'center' }}>
          <Button
            type="primary"
            icon={<FilePdfOutlined />}
            onClick={handleDownloadPdf}
            size="large"
            style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
          >
            Descargar PDF
          </Button>
          <Button
            icon={<FileTextOutlined />}
            onClick={handleDownloadXml}
            size="large"
          >
            Descargar XML
          </Button>
        </Space>

        <div style={{ padding: '0 16px' }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Enviar factura por correo:
          </Text>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              size="large"
            />
            <Button
              type="primary"
              icon={<MailOutlined />}
              onClick={handleSendEmail}
              loading={sendingEmail}
              disabled={!email}
              size="large"
              style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
            >
              Enviar
            </Button>
          </Space.Compact>
        </div>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Button type="link" onClick={onReset}>
            Facturar otra orden
          </Button>
        </div>
      </Space>
    </div>
  );
};

export default ResultStep;
